import { useState } from 'react';

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  borderStrong: 'rgba(247,246,242,0.25)',
  bgHover: 'rgba(247,246,242,0.06)',
};

function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m?.[1] || null;
}

function extractVimeoId(url: string): string | null {
  const m = url.match(/(?:player\.)?vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/);
  return m?.[1] || null;
}

type Source = 'youtube' | 'vimeo';

interface VideoAddModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: { videoUrl: string; thumbnailUrl: string; displayMode: string }) => void;
}

export function VideoAddModal({ open, onClose, onAdd }: VideoAddModalProps) {
  const [source, setSource] = useState<Source>('youtube');
  const [url, setUrl] = useState('');
  const [displayMode, setDisplayMode] = useState<'viewport' | 'background'>('viewport');

  if (!open) return null;

  const ytId = extractYouTubeId(url);
  const vimeoId = extractVimeoId(url);

  // Auto-detect source from pasted URL
  const detectedSource: Source | null = ytId ? 'youtube' : vimeoId ? 'vimeo' : null;
  const effectiveSource = detectedSource || source;

  const isValid = !!ytId || !!vimeoId;

  const thumbnail = ytId ? `https://img.youtube.com/vi/${ytId}/0.jpg` : null;

  const handleAdd = () => {
    if (!isValid) return;
    onAdd({ videoUrl: url.trim(), thumbnailUrl: thumbnail || '', displayMode });
    setUrl('');
    setDisplayMode('viewport');
    onClose();
  };

  const handleClose = () => {
    setUrl('');
    setDisplayMode('viewport');
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 13, fontWeight: 300,
    padding: '8px 12px', width: '100%',
    border: `1px solid ${C.border}`, backgroundColor: 'rgba(247,246,242,0.04)', color: C.fg,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={handleClose} />
      <div style={{ position: 'relative', width: 480, maxWidth: '90vw', backgroundColor: 'rgba(247,246,242,0.06)', border: `1px solid ${C.border}`, padding: 32 }}>
        <h3 style={{ fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: C.fg, textTransform: 'uppercase', marginBottom: 24, letterSpacing: '0.1em' }}>
          ADICIONAR VÍDEO
        </h3>

        {/* Source tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {(['youtube', 'vimeo'] as Source[]).map(s => (
            <button
              key={s}
              onClick={() => { setSource(s); setUrl(''); }}
              style={{
                fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                padding: '6px 16px', cursor: 'pointer',
                border: `1px solid ${effectiveSource === s ? C.fg : C.border}`,
                color: effectiveSource === s ? C.fg : C.fgMuted,
                backgroundColor: effectiveSource === s ? C.bgHover : 'transparent',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* URL input */}
        <label style={{ fontFamily: monoFont, fontSize: 12, color: C.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>
          URL do vídeo
        </label>
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Cole o link aqui" style={inputStyle} />

        {/* Preview */}
        {thumbnail && (
          <div style={{ marginTop: 16 }}>
            <img src={thumbnail} alt="Preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', border: `1px solid ${C.border}` }} />
          </div>
        )}

        {isValid && !thumbnail && (
          <div style={{ marginTop: 16, padding: 16, border: `1px solid ${C.border}`, textAlign: 'center' }}>
            <span style={{ fontFamily: monoFont, fontSize: 12, color: C.fgMuted }}>
              ✓ URL válida — Vimeo ID: {vimeoId}
            </span>
          </div>
        )}

        {/* Display mode */}
        <label style={{ fontFamily: monoFont, fontSize: 12, color: C.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginTop: 20, marginBottom: 6 }}>
          Posição na galeria
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { value: 'viewport', label: 'Ocupar viewport inteiro' },
            { value: 'background', label: 'Fundo da seção' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setDisplayMode(opt.value as 'viewport' | 'background')}
              style={{
                fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '6px 16px',
                cursor: 'pointer', flex: 1,
                border: `1px solid ${displayMode === opt.value ? C.fg : C.border}`,
                color: displayMode === opt.value ? C.fg : C.fgMuted,
                backgroundColor: displayMode === opt.value ? C.bgHover : 'transparent',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
          <button onClick={handleClose} style={{ fontFamily: monoFont, fontSize: 12, color: C.fgMuted, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase' }}>
            CANCELAR
          </button>
          <button
            onClick={handleAdd}
            disabled={!isValid}
            style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
              padding: '8px 20px', cursor: isValid ? 'pointer' : 'default',
              border: `1px solid ${C.borderStrong}`, color: C.fg,
              backgroundColor: 'transparent', opacity: isValid ? 1 : 0.3,
            }}
          >
            ADICIONAR
          </button>
        </div>
      </div>
    </div>
  );
}
